import { useCallback, useRef } from "react";

import { isAnchorAcknowledgementSubject } from "@/models/acknowledgement/acknowledgementStore";
import {
  type AcknowledgementIntent,
  acknowledgementWriteFor,
  recordAcknowledgements,
} from "@/models/acknowledgement/acknowledgementWrites";
import { latestItemForFeed } from "@/store/feedSelectors";
import {
  type AcknowledgementRequirement,
  DEFAULT_REQUIREMENT,
  feedWatermark,
  feedWatermarkThrough,
} from "./acknowledgements";
import { type AcknowledgeGrain, UNDO_LIMIT } from "./dispatchModel";
import type { DispatchTableRow } from "./dispatchTableRow";
import { rowFeedId } from "./dispatchTableRow";
import { type AcknowledgementTrigger, trackAcknowledgement } from "./dispatchTracking";

/** Who acknowledges, where, and what the view asks them to: what every write on the page is stamped with. */
export type AcknowledgementScope = {
  workspaceId: string;
  membershipId: string | undefined;
  /** What the view asks its team to acknowledge; a channel's watermark covers through the newest of it. */
  requires?: AcknowledgementRequirement;
};

/**
 * What a row's own acknowledgement targets at the message grain: the message on a message row, the event on an event
 * row, and on a driver row the driver's latest message. The driver's, not the channel's: after a reply the newest item
 * is the dispatcher's own, and handling that would clear nothing.
 */
function rowTarget(
  row: DispatchTableRow,
  feedId: string,
  action: AcknowledgementIntent["action"],
): AcknowledgementIntent | null {
  if (row.kind === "message") {
    const event = row.item.tmsEvent;
    if (row.item.source === "tmsEvent" && event && isAnchorAcknowledgementSubject(event.subject)) {
      return { subject: event.subject, action, anchorId: event.id };
    }
    return { subject: "feed_item", action, feedItemId: row.item.id };
  }
  const latest = latestItemForFeed(feedId, (item) => item.isFromAliasMember === true)?.id;
  return latest ? { subject: "feed_item", action, feedItemId: latest } : null;
}

/**
 * The inverse of an intent: the same target, the other action. What undo writes.
 *
 * A channel unacknowledge carries the `through` of the watermark it pops, so undoing it restores that watermark
 * exactly rather than covering everything that arrived since.
 */
function inverseOf(intent: AcknowledgementIntent): AcknowledgementIntent {
  const action = intent.action === "acknowledged" ? "unacknowledged" : "acknowledged";
  return { ...intent, action } as AcknowledgementIntent;
}

/**
 * The acknowledgements the page writes and the undo that takes them back.
 *
 * Every action is one row in its subject's acknowledgement log, recorded into the store at once and then to the
 * server (`acknowledgementWrites.ts`); the rows redraw from the store as each one lands. A channel acknowledgement is
 * a watermark through the newest arrival the page holds for it, never fanned out to the channel's items. Every
 * action this session takes leaves its inverse on the undo stack, {@link UNDO_LIMIT} deep, so Ctrl or Cmd with Z
 * takes back the newest. `acknowledgeRow` and `unacknowledgeRow` act at the view's grain: the row's channel on a
 * driver-per-row view, the one message or event on a message-per-row view; the feed pair always acts on the channel.
 * The plural forms take the ticked rows: each at the grain, or each row's channel once however many are ticked.
 * Every write names what triggered it, and reports one Amplitude event per target ({@link trackAcknowledgement});
 * an undo reports the inverse it writes under the `undo` trigger.
 */
export function useAcknowledgements(
  grain: AcknowledgeGrain,
  scope: AcknowledgementScope,
): {
  acknowledgeRow: (row: DispatchTableRow, feedId: string, trigger: AcknowledgementTrigger) => void;
  unacknowledgeRow: (row: DispatchTableRow, feedId: string, trigger: AcknowledgementTrigger) => void;
  acknowledgeFeed: (feedId: string, trigger: AcknowledgementTrigger) => void;
  unacknowledgeFeed: (feedId: string, trigger: AcknowledgementTrigger) => void;
  acknowledgeRows: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  unacknowledgeRows: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  acknowledgeChannelsOf: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  undo: () => void;
} {
  const { workspaceId, membershipId, requires = DEFAULT_REQUIREMENT } = scope;
  const undoRef = useRef<(() => void)[]>([]);

  const record = useCallback(
    (intents: readonly AcknowledgementIntent[], trigger: AcknowledgementTrigger, remember = true) => {
      if (intents.length === 0) return;
      const writer = { workspaceId, actorWorkspaceMembershipId: membershipId ?? null };
      recordAcknowledgements(writer, intents.map(acknowledgementWriteFor));
      for (const intent of intents) trackAcknowledgement(workspaceId, intent, trigger);
      if (!remember) return;
      undoRef.current.push(() => {
        const inverse = intents.map(inverseOf);
        recordAcknowledgements(writer, inverse.map(acknowledgementWriteFor));
        for (const intent of inverse) trackAcknowledgement(workspaceId, intent, "undo");
      });
      if (undoRef.current.length > UNDO_LIMIT) undoRef.current.shift();
    },
    [workspaceId, membershipId],
  );

  const feedIntent = useCallback(
    (feedId: string, action: AcknowledgementIntent["action"]): AcknowledgementIntent => {
      if (action === "unacknowledged") {
        // The server stores no `through` on an unacknowledge; it rides here only for the undo to restore.
        const popped = feedWatermark(feedId)?.through;
        return popped === undefined
          ? { subject: "feed", action, feedId }
          : { subject: "feed", action, feedId, through: popped };
      }
      const through = feedWatermarkThrough(feedId, requires);
      return through === null ? { subject: "feed", action, feedId } : { subject: "feed", action, feedId, through };
    },
    [requires],
  );

  const acknowledgeFeed = useCallback(
    (feedId: string, trigger: AcknowledgementTrigger) => record([feedIntent(feedId, "acknowledged")], trigger),
    [record, feedIntent],
  );
  const unacknowledgeFeed = useCallback(
    (feedId: string, trigger: AcknowledgementTrigger) => record([feedIntent(feedId, "unacknowledged")], trigger),
    [record, feedIntent],
  );

  const intentsFor = useCallback(
    (rows: readonly DispatchTableRow[], action: AcknowledgementIntent["action"]): AcknowledgementIntent[] =>
      rows.flatMap((row) => {
        const feedId = rowFeedId(row);
        if (!feedId) return [];
        if (grain === "channel") return [feedIntent(feedId, action)];
        return rowTarget(row, feedId, action) ?? [];
      }),
    [grain, feedIntent],
  );

  const acknowledgeRow = useCallback(
    (row: DispatchTableRow, _feedId: string, trigger: AcknowledgementTrigger) =>
      record(intentsFor([row], "acknowledged"), trigger),
    [record, intentsFor],
  );
  const unacknowledgeRow = useCallback(
    (row: DispatchTableRow, _feedId: string, trigger: AcknowledgementTrigger) =>
      record(intentsFor([row], "unacknowledged"), trigger),
    [record, intentsFor],
  );
  const acknowledgeRows = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) =>
      record(intentsFor(rows, "acknowledged"), trigger),
    [record, intentsFor],
  );
  const unacknowledgeRows = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) =>
      record(intentsFor(rows, "unacknowledged"), trigger),
    [record, intentsFor],
  );
  const acknowledgeChannelsOf = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => {
      const feedIds = [...new Set(rows.map(rowFeedId))].filter((feedId): feedId is string => feedId !== null);
      record(
        feedIds.map((feedId) => feedIntent(feedId, "acknowledged")),
        trigger,
      );
    },
    [record, feedIntent],
  );
  const undo = useCallback(() => {
    undoRef.current.pop()?.();
  }, []);

  return {
    acknowledgeRow,
    unacknowledgeRow,
    acknowledgeFeed,
    unacknowledgeFeed,
    acknowledgeRows,
    unacknowledgeRows,
    acknowledgeChannelsOf,
    undo,
  };
}
